import React, { createContext, useState, useEffect, useContext, useCallback, useMemo } from "react";

/**
 * Dostępność (WCAG) — rozwiązanie przeniesione 1:1 z mPracownika (AccessibilityContext),
 * z jedną pozycją WIĘCEJ na życzenie klienta: ZMNIEJSZONYM kontrastem. Wysoki kontrast
 * pomaga przy słabym wzroku, zmniejszony — przy nadwrażliwości na światło i migrenach;
 * oba tryby wykluczają się nawzajem, bo sterują tą samą osią.
 *
 * Mechanika jak w mPracowniku: stan trzymamy w localStorage (per przeglądarka, działa
 * także przed zalogowaniem — na ekranie logowania i w portalu gościa), a do CSS mówimy
 * atrybutami na <html> (data-font-scale / data-high-contrast / data-low-contrast /
 * data-reader-mode), na które reagują nadpisania zmiennych w global.css.
 */
const AccessibilityContext = createContext();

export const useAccessibility = () => useContext(AccessibilityContext);

// Klucze localStorage — te same nazwy co w mPracowniku (a11y-*), żeby zachowanie było
// przewidywalne dla osób znających tamten system; low-contrast jest nasz własny.
const FONT_SCALE_KEY = "a11y-font-scale";
const HIGH_CONTRAST_KEY = "a11y-high-contrast";
const LOW_CONTRAST_KEY = "a11y-low-contrast";
const READER_MODE_KEY = "a11y-reader-mode";

// Skala czcionki — zmienia html font-size, co skaluje wszystkie wartości rem.
// Stopnie i wartości identyczne z mPracownikiem (2 mniejsze, domyślny, 2 większe).
const FONT_SCALES = [
    { label: "A⁻⁻", value: 0.8125, htmlPx: 12.1875 }, // 81.25%
    { label: "A⁻", value: 0.875, htmlPx: 13.125 },    // 87.5%
    { label: "A", value: 1.0, htmlPx: 15 },            // 100% (domyślne)
    { label: "A⁺", value: 1.125, htmlPx: 16.875 },     // 112.5%
    { label: "AA", value: 1.25, htmlPx: 18.75 },       // 125%
];

const DEFAULT_SCALE_INDEX = 2; // "A" = 1.0x

const readInitialScaleIndex = () => {
    try {
        const stored = localStorage.getItem(FONT_SCALE_KEY);
        if (stored !== null) {
            const idx = parseInt(stored, 10);
            return idx >= 0 && idx < FONT_SCALES.length ? idx : DEFAULT_SCALE_INDEX;
        }
    } catch {
        /* localStorage niedostępny (tryb prywatny) — zostają wartości domyślne */
    }
    return DEFAULT_SCALE_INDEX;
};

const readStoredFlag = (key) => {
    try {
        return localStorage.getItem(key) === "true";
    } catch {
        return false;
    }
};

const writeStoredValue = (key, value) => {
    try {
        localStorage.setItem(key, String(value));
    } catch {
        /* brak zapisu = ustawienie nie przeżyje odświeżenia; to akceptowalne */
    }
};

export const AccessibilityProvider = ({ children }) => {
    const [fontScaleIndex, setFontScaleIndex] = useState(readInitialScaleIndex);
    const [highContrast, setHighContrast] = useState(() => readStoredFlag(HIGH_CONTRAST_KEY));
    const [lowContrast, setLowContrast] = useState(() => readStoredFlag(LOW_CONTRAST_KEY));
    const [readerMode, setReaderMode] = useState(() => readStoredFlag(READER_MODE_KEY));

    // Zastosuj skalę czcionki na <html>
    useEffect(() => {
        const scale = FONT_SCALES[fontScaleIndex];
        document.documentElement.style.fontSize = `${scale.htmlPx}px`;
        document.documentElement.setAttribute("data-font-scale", String(fontScaleIndex));
        writeStoredValue(FONT_SCALE_KEY, fontScaleIndex);
    }, [fontScaleIndex]);

    useEffect(() => {
        if (highContrast) {
            document.documentElement.setAttribute("data-high-contrast", "true");
        } else {
            document.documentElement.removeAttribute("data-high-contrast");
        }
        writeStoredValue(HIGH_CONTRAST_KEY, highContrast);
    }, [highContrast]);

    useEffect(() => {
        if (lowContrast) {
            document.documentElement.setAttribute("data-low-contrast", "true");
        } else {
            document.documentElement.removeAttribute("data-low-contrast");
        }
        writeStoredValue(LOW_CONTRAST_KEY, lowContrast);
    }, [lowContrast]);

    useEffect(() => {
        if (readerMode) {
            document.documentElement.setAttribute("data-reader-mode", "true");
        } else {
            document.documentElement.removeAttribute("data-reader-mode");
        }
        writeStoredValue(READER_MODE_KEY, readerMode);
    }, [readerMode]);

    const increaseFontScale = useCallback(() => {
        setFontScaleIndex((prev) => Math.min(prev + 1, FONT_SCALES.length - 1));
    }, []);

    const decreaseFontScale = useCallback(() => {
        setFontScaleIndex((prev) => Math.max(prev - 1, 0));
    }, []);

    // Kontrasty wykluczają się nawzajem — włączenie jednego gasi drugi, zamiast pozwolić
    // na stan „oba naraz", którego CSS nie definiuje.
    const toggleHighContrast = useCallback(() => {
        setHighContrast((prev) => {
            const next = !prev;
            if (next) setLowContrast(false);
            return next;
        });
    }, []);

    const toggleLowContrast = useCallback(() => {
        setLowContrast((prev) => {
            const next = !prev;
            if (next) setHighContrast(false);
            return next;
        });
    }, []);

    const toggleReaderMode = useCallback(() => {
        setReaderMode((prev) => !prev);
    }, []);

    const resetAll = useCallback(() => {
        setFontScaleIndex(DEFAULT_SCALE_INDEX);
        setHighContrast(false);
        setLowContrast(false);
        setReaderMode(false);
    }, []);

    const value = useMemo(() => ({
        fontScaleIndex,
        fontScaleLabel: FONT_SCALES[fontScaleIndex].label,
        highContrast,
        lowContrast,
        readerMode,
        canIncrease: fontScaleIndex < FONT_SCALES.length - 1,
        canDecrease: fontScaleIndex > 0,
        isDefault:
            fontScaleIndex === DEFAULT_SCALE_INDEX && !highContrast && !lowContrast && !readerMode,
        increaseFontScale,
        decreaseFontScale,
        toggleHighContrast,
        toggleLowContrast,
        toggleReaderMode,
        resetAll,
    }), [
        fontScaleIndex, highContrast, lowContrast, readerMode,
        increaseFontScale, decreaseFontScale,
        toggleHighContrast, toggleLowContrast, toggleReaderMode, resetAll,
    ]);

    return (
        <AccessibilityContext.Provider value={value}>
            {children}
        </AccessibilityContext.Provider>
    );
};

export default AccessibilityContext;
